import { createTheme } from '@mui/material/styles';

import borderRadius from 'styles/base/borderRadius_exported.module.scss';
import gaps from 'styles/base/gaps_exported.module.scss';
import palette from 'styles/base/palette.module.scss';

declare module '@mui/material/styles' {
  interface Palette {
    acid: Palette['primary'];
    brown: Palette['primary'];
    chocolate: Palette['primary'];
    darkBody: Palette['primary'];
    lightBody: Palette['primary'];
    lighter: Palette['primary'];
    skin: Palette['primary'];
    white: Palette['primary'];
    outlines: Palette['primary'];
  }

  interface PaletteOptions {
    acid?: PaletteOptions['primary'];
    brown?: PaletteOptions['primary'];
    chocolate?: PaletteOptions['primary'];
    darkBody?: PaletteOptions['primary'];
    lightBody?: PaletteOptions['primary'];
    lighter?: PaletteOptions['primary'];
    skin?: PaletteOptions['primary'];
    white?: PaletteOptions['primary'];
    outlines?: PaletteOptions['primary'];
  }
}

export interface BaseColorOverrides {
  acid: true;
  brown: true;
  chocolate: true;
  darkBody: true;
  lightBody: true;
  lighter: true;
  skin: true;
  white: true;
}

declare module '@mui/material/TextField' {
  export interface TextFieldPropsColorOverrides extends BaseColorOverrides {}
}

declare module '@mui/material/Chip' {
  export interface ChipPropsColorOverrides extends BaseColorOverrides {}
}

declare module '@mui/material/FormLabel' {
  export interface FormLabelPropsColorOverrides extends BaseColorOverrides {}
}

declare module '@mui/material/Chip' {
  interface ChipPropsVariantOverrides {
    taller: true;
    tallerOutlined: true;
  }
}

// We want to have this file here to avoid importing helpers/tests into the production bundle
export const testTheme = createTheme({
  palette: {
    acid: { main: palette.acid },
    brown: { main: palette.brown },
    chocolate: { main: palette.chocolate },
    darkBody: { main: palette.darkBody },
    lightBody: { main: palette.lightBody },
    lighter: { main: palette.lighter },
    primary: { main: palette.primary },
    secondary: { main: palette.secondary },
    outlines: { main: palette.colorSoftSensualOutlines },
    skin: { main: palette.skin },
    white: {
      main: palette.white,
      contrastText: palette.chocolate,
      dark: palette.white,
      light: palette.white,
    },
  },
});

export const theme = process.env.JEST_WORKER_ID
  ? testTheme // SCSS is not compiled in Jest, but we still need to have a full palette
  : createTheme({
      palette: {
        acid: { main: palette.acid },
        brown: { main: palette.brown },
        chocolate: { main: palette.chocolate },
        darkBody: { main: palette.darkBody },
        lightBody: { main: palette.lightBody },
        lighter: { main: palette.lighter },
        primary: { main: palette.primary },
        secondary: { main: palette.secondary },
        skin: { main: palette.skin },
        white: {
          main: palette.white,
          contrastText: palette.chocolate,
          dark: palette.white,
          light: palette.white,
        },
        outlines: { main: palette.colorSoftSensualOutlines },
        error: { main: palette.primary },
      },
      typography: {
        fontFamily: 'Figtree',
        fontSize: 16,
        fontWeightRegular: 400,
      },
      components: {
        MuiAccordion: {
          styleOverrides: {
            root: {
              paddingTop: gaps.gap32,
            },
          },
        },
        MuiBackdrop: {
          styleOverrides: {
            root: {
              backgroundColor: `${palette.darkBody}85`,
            },
          },
        },
        MuiGrid: {
          styleOverrides: {
            root: {
              display: 'flex',
              flexDirection: 'column',
              padding: 0,
              gap: gaps.defaultPageGap,
            },
          },
        },
        MuiFormLabel: {
          styleOverrides: {
            root: {
              '&.MuiInputLabel-root.Mui-error': {
                color: palette.darkBody, // override the red error color.
              },
            },
          },
        },
        MuiInputLabel: {
          styleOverrides: {
            root: {
              left: 6,
              color: palette.darkBody,
              fontWeight: 500,
            },
          },
        },
        MuiFilledInput: {
          styleOverrides: {
            root: {
              color: palette.darkBody,
              borderStyle: 'solid',
              backgroundColor: `${palette.white} !important`,
              borderRadius: borderRadius.borderRadius16,
              borderColor: palette.colorSoftSensualOutlines,
              fontFamily: 'Figtree',
              fontWeight: 600,
              overflow: 'hidden',
              paddingLeft: gaps.gap4,
              content: 'unset',
              '&:after, &:before': {
                content: 'unset',
              },
              '&.Mui-error': {
                borderColor: palette.acid,
              },
            },
          },
        },
        MuiInputBase: {
          styleOverrides: {
            input: {
              '&::placeholder': {
                color: palette.darkBody,
                fontFamily: 'Figtree',
                fontWeight: 500,
                opacity: 1,
              },
            },
          },
        },
        MuiChip: {
          styleOverrides: {
            root: {
              '&.MuiChip-colorSecondary': {
                backgroundColor: palette.lightBody,
                color: palette.darkBody,
              },
            },
          },
        },
        MuiButtonBase: {
          styleOverrides: {
            root: {
              '&.MuiMenuItem-root.Mui-selected': {
                backgroundColor: palette.colorSoftSensualOutlines,
              },
              '&.MuiMenuItem-root.Mui-selected:hover': {
                backgroundColor: palette.colorSoftSensualOutlines,
              },
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              '&.MuiPopover-paper.MuiMenu-paper': {
                color: palette.white,
                backgroundColor: palette.colorSoftSensualFg,
              },
              '& .MuiButtonBase-root.MuiMenuItem-root.Mui-selected': {
                color: palette.white,
                backgroundColor: palette.colorSoftSensualOutlines,
              },
              '& .MuiButtonBase-root.MuiMenuItem-root.Mui-selected:hover': {
                color: palette.white,
                backgroundColor: palette.colorSoftSensualOutlines,
              },
            },
          },
        },
      },
    });
